import type { FreightClaim } from "@freightsimple/generated-dashboard-openapi-client/models/FreightClaim";
import type { FreightClaimLineItem } from "@freightsimple/generated-dashboard-openapi-client/models/FreightClaimLineItem";
import { FreightClaimType } from "@freightsimple/generated-dashboard-openapi-client/models/FreightClaimType";
import { FreightClaimRepairability } from "@freightsimple/generated-dashboard-openapi-client/models/FreightClaimRepairability";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { formatPrice } from "@/Helpers/formatPrice";

interface AffectedFreightClaimItemProps {
  item: FreightClaimLineItem;
  freightClaim: FreightClaim;
}

const repairabilityLabels: Record<FreightClaimRepairability, string> = {
  [FreightClaimRepairability.Repairable]: " repairable",
  [FreightClaimRepairability.NotRepairable]: " not repairable",
  [FreightClaimRepairability.Unknown]: " unknown",
};

// Only a damage claim has anything to repair — a lost piece is just gone.
function repairabilityText(item: FreightClaimLineItem): string {
  if (
    item.claimType !== FreightClaimType.Damage ||
    item.repairability === undefined
  ) {
    return "";
  }
  return repairabilityLabels[item.repairability];
}

/** What was claimed against one line item: the money, the pieces, the freight. */
export function ViewShipmentsAffectedFreightClaimItem({
  item,
}: AffectedFreightClaimItemProps) {
  if (!item.unitCost || !item.piecesAffected) {
    return <></>;
  }

  const claimType =
    item.claimType === FreightClaimType.Loss ? "lost" : "damaged";
  const totalItem = item.unitCost * item.piecesAffected;
  const isSingleItem = item.piecesAffected === 1;
  const totalWeight =
    (item.weightPerHandlingUnit ?? 100) * (item.numberHandlingUnits ?? 1);

  const repairability = repairabilityText(item);
  const isNotRepairable =
    item.repairability === FreightClaimRepairability.NotRepairable;

  return (
    <Stack gap={0} align="start">
      <T size="base">
        Claim for ${formatPrice(totalItem)}
        {!isSingleItem && (
          <T size="sm" color="muted">
            {" "}
            &bull; ${formatPrice(item.unitCost)}/piece
          </T>
        )}
      </T>
      <T size="base">
        {isSingleItem ? (
          <>1 {item.condition?.toLowerCase()} piece </>
        ) : (
          <>
            {item.piecesAffected} of {item.piecesTotal}{" "}
            {item.condition?.toLowerCase()} pieces{" "}
          </>
        )}
        {/* The claim type is the word that matters in the sentence, so it takes
            the danger colour; the repairability only does when it's the answer
            that costs money. */}
        <T color="red">{claimType}</T>
        {repairability && (
          <T color={isNotRepairable ? "red" : "default"}>{repairability}</T>
        )}
      </T>
      <T size="sm" color="secondary">
        in {item.numberHandlingUnits} x {item.handlingUnitType}{" "}
        {item.description}
      </T>
      <T size="sm" color="secondary">
        {item.length}&quot;x{item.width}&quot;x{item.height}&quot;,{" "}
        {totalWeight}lb total
      </T>
    </Stack>
  );
}
